/**
 * Central Promotions Engine — configuration (Growth Platform — Slice S5).
 *
 * ONE reusable promotion architecture (spec §5, §6, §20, §24). All discounts —
 * special-group discounts, monthly campaigns, holiday campaigns, member-only
 * offers — are declared HERE as data, never hard-coded into individual pages.
 *
 * The UI reads promotions through src/utils/promotions.js (never this file
 * directly), so the same records can later be served from the MERN
 * /api/promotions endpoint and managed by an admin — with zero UI change.
 *
 * PROMOTION SCHEMA (mirrors spec §5 fields):
 *   id            unique slug
 *   title         short headline
 *   description   supporting copy
 *   type          "percentage" | "fixed" | "bundle" | "free_item" | "info"
 *   discountValue number (percent or dollars, per `type`)
 *   couponCode    optional code string (null = automatic / no code)
 *   startDate     ISO date string or null (null = always-on)
 *   endDate       ISO date string or null (null = no expiry)
 *   eligibility   "all" | "members" | "military" | "student" | "first_responder"
 *                 | "teacher" | "healthcare"  (spec §20)
 *   appliesTo     "repairs" | "accessories" | "all"
 *   status        "active" | "draft" | "archived"
 *   featured      boolean — surface on homepage / hero slots
 *   badge         { label, icon, tags[] } presentation hints
 *   terms         short terms/eligibility note
 *
 * IMPORTANT (spec rules 8–11): promotions with status "draft" NEVER render
 * publicly. Prices/percentages live here, not scattered in components.
 */

export const PROMOTIONS = [
  {
    id: "military-first-responder",
    title: "Military & First Responder Discount",
    description:
      "We proudly offer 10% OFF for all active duty, veterans, police, firefighters & EMTs.",
    type: "percentage",
    discountValue: 10,
    couponCode: null,
    startDate: null,
    endDate: null,
    eligibility: "first_responder",
    appliesTo: "all",
    status: "active",
    featured: true,
    badge: {
      label: "10% OFF",
      icon: "fas fa-medal",
      tags: [
        { label: "Military", icon: "fas fa-flag-usa", color: "#ffaa19" },
        { label: "Police", icon: "fas fa-shield-alt", color: "#4299e1" },
        { label: "Firefighters", icon: "fas fa-fire-extinguisher", color: "#fc8181" },
        { label: "EMTs", icon: "fas fa-ambulance", color: "#68d391" },
      ],
    },
    terms: "Valid ID required. Cannot be combined with other offers.",
  },
  {
    id: "student-discount",
    title: "Student Discount",
    description:
      "Students save 10% on repairs — just show a valid student ID in store.",
    type: "percentage",
    discountValue: 10,
    couponCode: null,
    startDate: null,
    endDate: null,
    eligibility: "student",
    appliesTo: "repairs",
    status: "active",
    featured: false,
    badge: {
      label: "10% OFF",
      icon: "fas fa-user-graduate",
      tags: [{ label: "Students", icon: "fas fa-graduation-cap", color: "#ffaa19" }],
    },
    terms: "Valid student ID required. In-store only.",
  },
  // Example of a scheduled/draft campaign — NOT shown publicly until an admin
  // sets status:"active" and the current date falls in the window. Left here
  // as a template for the monthly/holiday calendar (spec §6, §24).
  {
    id: "back-to-school-2026",
    title: "Back-to-School Screen Protector Deal",
    description:
      "Free premium screen protector installation with any screen repair.",
    type: "free_item",
    discountValue: 0,
    couponCode: "BTS2026",
    startDate: "2026-08-01",
    endDate: "2026-09-15",
    eligibility: "all",
    appliesTo: "repairs",
    status: "draft",
    featured: false,
    badge: {
      label: "FREE INSTALL",
      icon: "fas fa-shield-alt",
      tags: [],
    },
    terms: "With qualifying screen repair. Limited time.",
  },
];
